import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';

/**
 * LOBBERO STAYS — calendar sync, the host's side (plan S5;
 * lobbero_be/src/stays/routes/ical.js). The DEFAULT axios instance like the
 * other Stays clients (cookies, CSRF, refresh).
 *
 * Every failure is a StaysSyncError: S1's StaysApiError plus
 *   - `reason`: the fetch result word of a refused link test (timeout,
 *     http_error, not_ical, …) — utils/stays-sync.ts turns it into words;
 *   - `retryAfter`: the seconds a "Sync now" must wait (429 SYNC_TOO_SOON).
 *
 * NEVER STORED: an export link's URL is in the create answer only (the server
 * never shows it again); this client hands it to the caller and keeps nothing.
 */

export type SyncProvider = 'airbnb' | 'booking' | 'vrbo' | 'other';
export type SyncDestination = 'airbnb' | 'booking' | 'vrbo' | 'personal';
export type SyncAmbiguousAs = 'ask' | 'reservation' | 'block';
export type SyncHealthState = 'ok' | 'stale' | 'failing' | 'paused' | 'never';
export type SyncReader = 'airbnb' | 'booking' | 'vrbo' | 'google' | 'apple' | 'microsoft' | 'other';
export type SyncFetchResult =
  | 'ok'
  | 'not_modified'
  | 'http_error'
  | 'timeout'
  | 'not_ical'
  | 'too_large'
  | 'too_many_events'
  | 'blocked_address'
  | 'dns_error'
  | 'tls_error'
  | 'redirect_limit'
  | 'parse_error'
  | 'no_network';

export interface SyncFeedHealth {
  state: SyncHealthState;
  last_success_at: string | null;
  last_fetch_at: string | null;
  last_result: SyncFetchResult | null;
  last_http_status: number | null;
  consecutive_failures: number;
  suspect_empty: boolean;
  next_fetch_at: string | null;
  events_seen: number | null;
}

export interface SyncFeed {
  id: string;
  listing_id: string;
  provider: SyncProvider;
  provider_name: string;
  label: string | null;
  url_host: string;
  /** host + "…" + the last 4 characters of the path — never the link itself. */
  url_hint: string;
  paused: boolean;
  auto_approve: boolean;
  ambiguous_as: SyncAmbiguousAs;
  version: number;
  health: SyncFeedHealth;
}

export interface SyncExport {
  id: string;
  destination: SyncDestination;
  created_at: string | null;
  last_read_at: string | null;
  last_reader: SyncReader | null;
  read_count: number;
}

export type SyncReviewKind = 'new' | 'date_update' | 'conflict' | 'missing_from_feed' | 'cancelled_by_provider';
export type SyncReviewState = 'pending' | 'approved' | 'auto_approved' | 'dismissed' | 'superseded';

export interface SyncReviewCounts {
  new: number;
  date_update: number;
  conflict: number;
  missing_from_feed: number;
  cancelled_by_provider: number;
}

export interface CalendarSyncPage {
  listing_id: string;
  building_today: string;
  tz?: string | null;
  tz_source?: string | null;
  feeds: SyncFeed[];
  exports: SyncExport[];
  reviews: { pending: number; by_kind: SyncReviewCounts };
  guest_name_required: number;
  /** Stays from a booking site created in the last 90 days (K-63's banner hides when > 0). */
  imported_recent: number;
  listing_status: string;
  cadence_minutes: number;
  can_manage: boolean;
}

export interface ConnectFeedInput {
  listing_id: string;
  provider: SyncProvider;
  url: string;
  label?: string | null;
  auto_approve?: boolean;
  ambiguous_as?: SyncAmbiguousAs;
}

export interface FirstSync {
  result: SyncFetchResult;
  events: number;
  stays_found: number;
  blocks_found: number;
  reviews_created: number;
  auto_applied: number;
}

export interface FeedSettingsInput {
  version: number;
  label?: string | null;
  paused?: boolean;
  auto_approve?: boolean;
  ambiguous_as?: SyncAmbiguousAs;
  url?: string;
}

export interface SyncNowResult {
  result: SyncFetchResult;
  events: number | null;
  changes: { reviews_created: number; auto_applied: number; blocks_added: number; blocks_removed: number; missing_marked: number };
}

export interface SyncReviewDates {
  check_in: string;
  check_out: string;
  nights?: number;
  as?: 'reservation' | 'block' | 'ask';
  restore?: boolean;
}

export interface SyncReviewConflict {
  type: 'reservation' | 'block';
  id: string;
  check_in: string;
  check_out: string;
}

export interface SyncReview {
  id: string;
  version: number;
  kind: SyncReviewKind;
  state: SyncReviewState;
  provider: SyncProvider;
  listing: { id: string; name: string | null };
  proposed: SyncReviewDates;
  previous: SyncReviewDates | null;
  conflicts: SyncReviewConflict[];
  event: {
    external_ref: string | null;
    phone_last4_masked: string | null;
    summary_kind: string;
    status_raw: string | null;
    uid_synthesized: boolean;
  } | null;
  reservation: {
    id: string;
    check_in: string;
    check_out: string;
    status: string;
    guest_display: string;
    guest_name_required: boolean;
  } | null;
  created_at: string | null;
  decided_at: string | null;
  decided_by_name: string | null;
}

export interface SyncFailingFeed {
  feed_id: string;
  listing_id: string;
  listing_name: string | null;
  provider: SyncProvider;
  last_success_at: string | null;
}

export interface SyncReviewPage {
  items: SyncReview[];
  next_cursor: string | null;
  counts: SyncReviewCounts & { pending: number };
  /** The banners' facts for the same scope (screens §4). */
  health: { failing: SyncFailingFeed[]; guest_name_required: number };
  can_manage: boolean;
}

export interface SyncReviewQuery {
  listing_id?: string;
  building_id?: number;
  state?: 'pending' | 'recent';
  cursor?: string | null;
}

export interface SyncDecisionEdits {
  as?: 'reservation' | 'block';
  check_in?: string;
  check_out?: string;
  lead_first_name?: string;
  lead_last_name?: string;
  lead_email?: string;
  lead_phone?: string;
  party_size?: number;
  notes?: string;
}

export interface SyncDecisionResult {
  review: SyncReview;
  reservation?: { id: string };
  block?: { id: string; start_date: string; end_date: string };
}

export class StaysSyncError extends StaysApiError {
  reason: SyncFetchResult | null;
  retryAfter: number | null;

  constructor(status: number, body: Record<string, unknown> | null, fallback: string) {
    super(status, body, fallback);
    this.name = 'StaysSyncError';
    this.reason = body && typeof body.result === 'string' ? (body.result as SyncFetchResult) : null;
    this.retryAfter = body && typeof body.retry_after_s === 'number' ? body.retry_after_s : null;
  }
}

const call = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysSyncError(err.response.status, err.response.data || null, fallback);
    throw new StaysSyncError(0, null, fallback);
  }
};

const listing = (id: string) => `${apiRoute}/stays/listings/${encodeURIComponent(id)}`;
const feed = (id: string) => `${apiRoute}/stays/ical-feeds/${encodeURIComponent(id)}`;
const review = (id: string) => `${apiRoute}/stays/sync-reviews/${encodeURIComponent(id)}`;

// ── the listing's tab ─────────────────────────────────────────────────────
export const getCalendarSync = (listingId: string): Promise<CalendarSyncPage> =>
  call(axios.get(`${listing(listingId)}/calendar-sync`), 'Could not load the calendar sync.');

// ── feeds ──────────────────────────────────────────────────────────────────
export const connectFeed = (input: ConnectFeedInput): Promise<{ feed: SyncFeed; first_sync: FirstSync }> =>
  call(axios.post(`${apiRoute}/stays/ical-feeds`, input), 'Could not connect the calendar.');

export const updateFeed = (feedId: string, input: FeedSettingsInput): Promise<{ feed: SyncFeed }> =>
  call(axios.patch(feed(feedId), input), 'Could not save the calendar settings.');

export const removeFeed = (
  feedId: string
): Promise<{ removed: boolean; blocks_removed: number; reviews_superseded: number }> =>
  call(axios.delete(feed(feedId)), 'Could not remove the calendar.');

export const syncFeedNow = (feedId: string): Promise<SyncNowResult> =>
  call(axios.post(`${feed(feedId)}/sync`, {}), 'Could not sync the calendar.');

// ── reviews ────────────────────────────────────────────────────────────────
export const listSyncReviews = (query: SyncReviewQuery = {}): Promise<SyncReviewPage> => {
  const params: Record<string, string> = {};
  if (query.listing_id) params.listing_id = query.listing_id;
  if (query.building_id != null) params.building_id = String(query.building_id);
  if (query.state) params.state = query.state;
  if (query.cursor) params.cursor = query.cursor;

  return call(axios.get(`${apiRoute}/stays/sync-reviews`, { params }), 'Could not load the calendar changes.');
};

export const decideSyncReview = (
  reviewId: string,
  input: { decision: 'approve' | 'dismiss'; version: number; edits?: SyncDecisionEdits }
): Promise<SyncDecisionResult> =>
  call(axios.post(`${review(reviewId)}/decide`, input), 'Could not save your answer.');

export const approveSyncReviews = (
  items: Array<{ id: string; version: number }>
): Promise<{ results: Array<{ id: string; ok: boolean; code?: string }> }> =>
  call(axios.post(`${apiRoute}/stays/sync-reviews/decide-batch`, { decision: 'approve', items }), 'Could not approve the changes.');

// ── export links ───────────────────────────────────────────────────────────
/** Create or replace the listing's link for one destination. The URL is returned this once. */
export const createExportLink = (
  listingId: string,
  destination: SyncDestination
): Promise<{ export: { id: string; destination: SyncDestination; created_at: string | null }; url: string }> =>
  call(axios.post(`${listing(listingId)}/ical-exports`, { destination }), 'Could not make the link.');

export const revokeExportLink = (exportId: string): Promise<{ revoked: boolean }> =>
  call(axios.delete(`${apiRoute}/stays/ical-exports/${encodeURIComponent(exportId)}`), 'Could not turn the link off.');
